import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import type { CountryCode } from 'src/api/endpoints/myRmApi.types';
import { COUNTRIES } from 'src/utils/countryList';
import { NoUserError } from 'src/utils/noUserError';
import { userHelpers } from 'src/utils/userHelpers';
import { z } from 'zod';

// Spec: https://getremarkable.atlassian.net/wiki/spaces/RS/pages/6485344281/Enterprise+device+sales+EBPF+Integration

const availableCountries = COUNTRIES.map((country) => country.value);

const billingAddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  /** Validate to be a country we support (COUNTRIES) */
  country: z
    .string()
    .refine((val) => availableCountries.includes(val as CountryCode), {
      message: 'Invalid country',
    })
    .optional(),
});

export const setupEnrollmentFormSchema = z.object({
  companyName: z.string().optional(),
  billingEmail: z.string().email().optional(),
  duns: z.string().optional(),
  taxId: z.string().optional(),
  billingAddress: billingAddressSchema.optional(),
  orderId: z.string().optional(),
});

export type SetupEnrollmentFormData = z.infer<typeof setupEnrollmentFormSchema>;

const linkSearchParams = z.object({
  data: setupEnrollmentFormSchema.optional(),
});

export const Route = createFileRoute('/_auth/enterprise/link')({
  errorComponent: () => <div>Failed to load enterprise link data.</div>,
  validateSearch: zodValidator(linkSearchParams),
  beforeLoad: async ({ search, context }) => {
    const auth = await context.auth;

    if (!auth.user) {
      throw new NoUserError();
    }

    if (userHelpers.isOrganizationMember(auth.user)) {
      throw redirect({
        to: '/enterprise',
        replace: true,
      });
    }

    throw redirect({
      to: '/enterprise/enroll/create-organization',
      search: search.data ? { defaultValues: search.data } : undefined,
      replace: true,
    });
  },
});
